import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { apiClient } from "../lib/api";
import { useAuth } from "./AuthContext";

const InstrumentCtx = createContext(null);

// Display labels (the backend stores lowercase keys like "upright_bass")
const LABELS = {
  piano: "Piano",
  guitar: "Guitar",
  violin: "Violin",
  cello: "Cello",
  upright_bass: "Upright Bass",
  drums: "Drums",
  flute: "Flute",
  saxophone: "Saxophone",
  trumpet: "Trumpet",
  clarinet: "Clarinet",
  voice: "Voice",
  ukulele: "Ukulele",
};

const EMOJI = {
  piano: "🎹", guitar: "🎸", violin: "🎻", cello: "🎻",
  upright_bass: "🎻", drums: "🥁", flute: "🪈", saxophone: "🎷",
  trumpet: "🎺", clarinet: "🎶", voice: "🎤", ukulele: "🪕",
};

// Fighter-select palette: bold, distinct, high-contrast color per instrument.
// [primary, secondary (for gradient end), foreground text color]
const PALETTE = {
  piano:        ["#1E1E2E", "#3A3A5C", "#FFD166"],
  guitar:       ["#8B2500", "#C94B20", "#FFF3D6"],
  violin:       ["#4A1C6E", "#8B3FAA", "#FFE6FF"],
  cello:        ["#5C1E3D", "#A04772", "#FFE4EA"],
  upright_bass: ["#2B2B2B", "#505050", "#FFB703"],
  drums:        ["#B00020", "#FF5B5B", "#FFFFFF"],
  flute:        ["#004C6D", "#2E8AB8", "#E6F9FF"],
  saxophone:    ["#A35200", "#E08E28", "#FFF4D6"],
  trumpet:      ["#7A4A00", "#D4A24C", "#FFF8E7"],
  clarinet:     ["#0E0E0E", "#3A3A3A", "#F4E9D8"],
  voice:        ["#7A0B52", "#D94FAD", "#FFE6F6"],
  ukulele:      ["#4C6B1F", "#90BC3D", "#F8FFD6"],
};

// Studio Pro custom-instrument palette (shared by all custom instruments)
const CUSTOM_PALETTE = ["#5A2A82", "#9B59D6", "#FFF0FF"];

export function instrumentLabel(key, customMap) {
  if (!key) return "Instrument";
  if (key.startsWith("custom_") && customMap && customMap[key]?.label) return customMap[key].label;
  return LABELS[key] || key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export function instrumentEmoji(key, customMap) {
  if (key && key.startsWith("custom_") && customMap && customMap[key]?.emoji) return customMap[key].emoji;
  return EMOJI[key] || "🎼";
}

export function instrumentPalette(key) {
  if (key && key.startsWith("custom_")) return CUSTOM_PALETTE;
  return PALETTE[key] || ["#333333", "#666666", "#FFFFFF"];
}

export function InstrumentProvider({ children }) {
  const { user } = useAuth();
  const initialInstruments = (user && user.instruments && user.instruments.length) ? user.instruments : [];
  const [instruments, setInstruments] = useState(initialInstruments);
  const [catalog, setCatalog] = useState([]);
  const [customInstruments, setCustomInstruments] = useState({});
  const [ready, setReady] = useState(initialInstruments.length > 0);
  const storageKey = user ? `mptp_current_instrument_${user.id}` : null;
  const [current, setCurrent] = useState(() => {
    const cached = storageKey && localStorage.getItem(storageKey);
    if (cached) return cached;
    return initialInstruments[0] || null;
  });

  // Keep local state in sync whenever the auth user changes (login, refresh, upgrade-to-teacher)
  useEffect(() => {
    const list = (user && user.instruments && user.instruments.length) ? user.instruments : [];
    if (list.length) {
      setInstruments(list);
      setReady(true);
      const cached = storageKey && localStorage.getItem(storageKey);
      if (!cached || !list.includes(cached)) {
        setCurrent(list[0]);
        if (storageKey) localStorage.setItem(storageKey, list[0]);
      }
    }
  }, [user, storageKey]);

  const refresh = useCallback(async () => {
    if (!user) { setInstruments([]); setCustomInstruments({}); setReady(false); return; }
    try {
      const r = await apiClient.get("/me/instruments");
      const list = r.data.instruments || [];
      setInstruments(list);
      setCatalog(r.data.catalog || []);
      setCustomInstruments(r.data.custom_instruments || {});
      setReady(true);
      if (list.length === 0) {
        setCurrent(null);
        return;
      }
      // Pick current from storage, else first
      const cached = storageKey && localStorage.getItem(storageKey);
      if (!cached || !list.includes(cached)) {
        setCurrent(list[0]);
        if (storageKey) localStorage.setItem(storageKey, list[0]);
      } else {
        setCurrent(cached);
      }
    } catch (e) { console.warn("Instruments refresh failed", e); }
  }, [user, storageKey]);

  useEffect(() => { refresh(); }, [refresh]);

  const setCurrentAndPersist = useCallback((key) => {
    setCurrent(key);
    if (storageKey) localStorage.setItem(storageKey, key);
  }, [storageKey]);

  // Resolver functions that close over the user's custom_instruments dict
  const labelFor = useCallback((k) => instrumentLabel(k, customInstruments), [customInstruments]);
  const emojiFor = useCallback((k) => instrumentEmoji(k, customInstruments), [customInstruments]);

  const value = useMemo(() => ({
    instruments, current, setCurrent: setCurrentAndPersist, refresh, catalog, ready,
    customInstruments,
    label: labelFor, emoji: emojiFor,
  }), [instruments, current, setCurrentAndPersist, refresh, catalog, ready, customInstruments, labelFor, emojiFor]);

  return <InstrumentCtx.Provider value={value}>{children}</InstrumentCtx.Provider>;
}

export const useInstrument = () => useContext(InstrumentCtx) || { instruments: [], current: "piano", setCurrent: () => {}, refresh: () => {}, catalog: [], customInstruments: {}, ready: false, label: instrumentLabel, emoji: instrumentEmoji };
